import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import SubjectIcon from '@mui/icons-material/Subject';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useMailTemplate from '../../hooks/useMailTemplate';
import MailBlockEditor from '../MailEditor/MailBlockEditor';
import PreviewDialog from '../MailEditor/PreviewDialog';
import { MailEditorConfigProvider } from '../MailEditor/mailEditorConfig';
import {
  buildTemplateUpdatePayload,
  collectVariableTokens,
  deepClone,
  normalizeTemplateDoc,
} from '../MailEditor/editorUtils';

// Subject line and plain-text version of a transactional template. Both are
// edited outside the canvas because they are not part of the visual layout.
const SubjectAndTextDialog = ({ open, doc, onClose, onCommit, disabled }) => {
  const [subject, setSubject] = useState(doc.subject || '');
  const [textContent, setTextContent] = useState(doc.textContent || '');

  useEffect(() => {
    if (!open) return;
    setSubject(doc.subject || '');
    setTextContent(doc.textContent || '');
  }, [open, doc.subject, doc.textContent]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Betreff und Text-Version</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          label='Betreff'
          size='small'
          fullWidth
          disabled={disabled}
          value={subject}
          onChange={event => setSubject(event.target.value)}
        />
        <TextField
          label='Text-Version'
          size='small'
          fullWidth
          multiline
          minRows={12}
          disabled={disabled}
          value={textContent}
          onChange={event => setTextContent(event.target.value)}
          helperText='Wird an Empfänger ausgeliefert, die kein HTML anzeigen können.'
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          disabled={disabled}
          onClick={() => {
            onCommit({ subject, textContent });
            onClose();
          }}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Editor for a transactional mail template. It uses the same Baukasten as the
// marketing mail editor; the blocks are compiled into the content fragment that
// the branded mail shell wraps when the mail is sent.
const MailTemplateEditor = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const navigate = useNavigate();
  const authCtx = useAuth();
  const { getTemplateById, updateTemplate, previewTemplateDoc, uploadAsset, getAssets, deleteAsset } =
    useMailTemplate();

  const [doc, setDoc] = useState(null);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  const canUpdate = authCtx.checkRight('mailTemplate:update');

  useEffect(() => {
    if (!templateId) return;
    const load = async () => {
      try {
        const template = await getTemplateById(templateId);
        if (!template) return;
        setDoc(normalizeTemplateDoc(template));
        setAvailableTokens(
          collectVariableTokens(template.htmlContent, template.textContent, template.subject)
        );
      } catch (error) {
        toast.error('Fehler beim Laden des Templates');
        setLoadFailed(true);
      }
    };
    load();
  }, [templateId, getTemplateById]);

  const mutateDoc = useCallback(mutator => {
    setDoc(prev => {
      if (!prev) return prev;
      const next = deepClone(prev);
      mutator(next);
      return next;
    });
    setDirty(true);
  }, []);

  const editorConfig = useMemo(
    () => ({
      // Transactional variables depend on the mail the template is used for, so
      // the picker offers the placeholders this template already works with.
      variables: availableTokens.map(token => ({ token })),
      variableHint:
        'Platzhalter wie {{kundenname}} und Bedingungen wie {{#if ...}} bleiben unverändert und werden erst beim Versand ersetzt.',
      uploadAsset,
      listAssets: getAssets,
      deleteAsset,
      renderPreview: previewTemplateDoc,
      showFooterBlock: false,
      showMessageStyle: true,
      showFallbacks: false,
      savedElements: null,
    }),
    [availableTokens, uploadAsset, getAssets, deleteAsset, previewTemplateDoc]
  );

  const handleSave = useCallback(async () => {
    if (!doc) return false;
    setSaving(true);
    try {
      const updated = await updateTemplate(templateId, buildTemplateUpdatePayload(doc));
      if (updated === null) return false;
      setDirty(false);
      toast.success('Template gespeichert');
      return true;
    } catch (error) {
      toast.error(
        error?.code === 'MAIL_TEMPLATE_CONTENT_EMPTY'
          ? 'Die Mailvorlage hat noch keinen Inhalt.'
          : 'Fehler beim Speichern des Templates'
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [doc, templateId, updateTemplate]);

  const handleBack = () => {
    if (dirty) setLeaveDialogOpen(true);
    else navigate('/mailTemplateManagement');
  };

  // Access rights
  if (!authCtx.checkRight('mailTemplate:read')) {
    return <Navigate to='/welcome' replace />;
  }

  if (!templateId) return <Navigate to='/mailTemplateManagement' replace />;

  if (loadFailed) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ mb: 2 }}>Das Template konnte nicht geladen werden.</Typography>
        <Button variant='outlined' onClick={() => navigate('/mailTemplateManagement')}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  if (!doc) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MailEditorConfigProvider config={editorConfig}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 72px)', minHeight: 480 }}>
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexWrap: 'wrap',
          }}
        >
          <Tooltip title='Zurück zur Übersicht'>
            <IconButton onClick={handleBack} aria-label='Zurück'>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant='subtitle1' noWrap sx={{ fontWeight: 600, maxWidth: 320 }}>
            {doc.key || 'Mailvorlage'}
          </Typography>
          {dirty && <Chip label='Ungespeicherte Änderungen' size='small' color='warning' variant='outlined' />}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='outlined'
            startIcon={<SubjectIcon />}
            onClick={() => setSubjectDialogOpen(true)}
          >
            Betreff & Text
          </Button>
          <Button variant='outlined' startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen(true)}>
            Vorschau
          </Button>
          <Button
            variant='contained'
            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />}
            disabled={!canUpdate || saving}
            onClick={handleSave}
          >
            Speichern
          </Button>
        </Box>

        <Alert severity='info' variant='outlined' sx={{ mx: 2, mt: 1, py: 0 }}>
          Kopf- und Fußzeile mit Logo und Impressum kommen aus dem Mail-Layout und sind hier nicht
          Teil des Baukastens.
        </Alert>

        <MailBlockEditor doc={doc} onMutate={mutateDoc} canEdit={canUpdate} />

        {/* Dialogs */}
        <PreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} emailDoc={doc} />
        <SubjectAndTextDialog
          open={subjectDialogOpen}
          doc={doc}
          disabled={!canUpdate}
          onClose={() => setSubjectDialogOpen(false)}
          onCommit={patch => mutateDoc(draft => Object.assign(draft, patch))}
        />
        <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)} maxWidth='xs' fullWidth>
          <DialogTitle>Änderungen verwerfen?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Es gibt ungespeicherte Änderungen. Möchten Sie diese speichern, bevor Sie den Editor
              verlassen?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveDialogOpen(false)}>Abbrechen</Button>
            <Button
              color='error'
              onClick={() => {
                setLeaveDialogOpen(false);
                navigate('/mailTemplateManagement');
              }}
            >
              Verwerfen
            </Button>
            <Button
              variant='contained'
              disabled={!canUpdate || saving}
              onClick={async () => {
                const ok = await handleSave();
                setLeaveDialogOpen(false);
                if (ok) navigate('/mailTemplateManagement');
              }}
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MailEditorConfigProvider>
  );
};

export default MailTemplateEditor;
